import { AccountTypeBadge } from "@shared/components/Yaas/AccountTypeBadge";
import { RetryIcon } from "@solomon-labs/design/components/icons";
import { Button, Text } from "@solomon-labs/design/components/ui";
import React from "react";

export interface DestinationCheckNoticeProps {
    /** Resolved copy for the current verification state. Null renders
     *  nothing — the destination needs no commentary. */
    message: string | null;
    isError: boolean;
    /** Only the retryable states (`lookup-failed`, `vault-unknown-to-system`)
     *  offer this. */
    canRetry: boolean;
    onRetry: () => void;
    /** Present only on a resolved Squads multisig. */
    multisig?: { threshold: number; memberCount: number } | null;
}

/**
 * The line under the Rewards Destination field reporting what the
 * Squads preflight found. Lifted out of `EnableRewardsForm` verbatim so
 * every state is reachable in `/showcase/enrollment-errors` — the error
 * states arrive only from a failing RPC, which no `?state=` frame can
 * produce.
 *
 * Presentational only: `useSquadsDestinationVerification` owns the state
 * machine and hands the resolved copy down.
 */
export function DestinationCheckNotice({
    message,
    isError,
    canRetry,
    onRetry,
    multisig = null
}: DestinationCheckNoticeProps): React.ReactElement | null {
    if (!message) return null;
    return (
        // One row, matching the field's own status row. A resolved
        // multisig puts its shape in a badge on the right, the way
        // "Valid account" pairs with the Wallet badge. A retryable
        // failure keeps Retry beside the message instead.
        <div
            // `w-full`: this renders as a flex ITEM inside the field's own
            // status row, so without it the row is only as wide as its
            // content and `justify-between` has nothing to push against.
            className={`flex min-h-7 w-full items-center gap-2 ${
                multisig ? "justify-between" : ""
            }`}
            role="status"
            aria-live="polite"
        >
            <Text
                variant="fine-print"
                className={
                    isError
                        ? "text-error-base"
                        : multisig
                          ? "text-success-base"
                          : "text-foreground-neutral"
                }
            >
                {message}
            </Text>
            {multisig ? (
                <AccountTypeBadge type="squads-multisig" multisig={multisig} />
            ) : (
                canRetry && (
                    <Button
                        variant="link"
                        size="2xs"
                        trailing={<RetryIcon />}
                        onClick={onRetry}
                    >
                        Retry
                    </Button>
                )
            )}
        </div>
    );
}
